<template>
  <Header />

  <div class="min-h-screen relative overflow-hidden">
    <!-- Background using login.png -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style="background-image: url('/login.png')"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>

    <!-- Decorative floating shapes -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Star shape top left -->
      <div class="absolute top-20 left-16 text-yellow-400 text-3xl animate-pulse">✦</div>
      <!-- Circle top right -->
      <div class="absolute top-32 right-20 w-4 h-4 bg-pink-400 rounded-full animate-bounce"></div>
      <!-- Plus bottom left -->
      <div class="absolute bottom-40 left-12 text-green-400 text-2xl animate-pulse">✚</div>
      <!-- Diamond right -->
      <div
        class="absolute top-1/2 right-16 w-6 h-6 bg-purple-400 transform rotate-45 animate-pulse"
      ></div>
      <!-- Circle bottom right -->
      <div
        class="absolute bottom-20 right-32 w-5 h-5 bg-orange-400 rounded-full animate-bounce"
      ></div>
      <!-- Clover left -->
      <div class="absolute top-1/3 left-20 text-green-500 text-2xl animate-pulse">♣</div>
      <!-- Star right -->
      <div class="absolute top-1/4 right-1/4 text-white text-xl animate-pulse">✦</div>
    </div>

    <!-- Content -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-32 lg:px-8">
      <div class="w-full max-w-md mt-15">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-5xl font-black text-white mb-4 tracking-wide">Reset Your Password</h1>
          <p class="text-xl text-white font-light italic tracking-wide">
            Enter your email to receive a reset link.
          </p>
        </div>

        <!-- Password Reset Request Form Container -->
        <div
          class="login-container bg-white rounded-3xl shadow-2xl p-10 border-4 border-black relative"
        >
          <form class="space-y-5" @submit.prevent="handlePasswordResetRequest">
            <!-- Email Field -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-600 mb-2">
                Email Address
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <input
                  v-model="email"
                  @input="validateEmail"
                  @blur="validateEmail"
                  type="email"
                  name="email"
                  id="email"
                  autocomplete="email"
                  required
                  placeholder="Enter your email address"
                  :class="[
                    'block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50',
                    emailError ? 'border-red-500' : 'border-gray-300'
                  ]"
                />
              </div>
              <!-- Email Error Message -->
              <div v-if="emailError" class="mt-1 text-red-600 text-sm">
                {{ emailError }}
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="errorMessage"
              class="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div
              v-if="successMessage"
              class="text-green-600 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2"
            >
              {{ successMessage }}
            </div>

            <!-- Info Message about Link Expiration -->
            <div
              class="text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-start"
            >
              <svg
                class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <strong>Important:</strong> The password reset link will expire in <strong>1 hour</strong> for security reasons. Make sure to reset your password within this time frame.
              </div>
            </div>

            <!-- Send Reset Link Button -->
            <div class="pt-4">
              <button
                type="submit"
                :disabled="!email.trim() || isSending || !!emailError"
                class="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 shadow-lg text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span v-if="isSending">Sending Reset Link...</span>
                <span v-else class="flex items-center">
                  Send Reset Link
                  <svg
                    class="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </form>

          <!-- Back to Login -->
          <div class="mt-6 text-center">
            <router-link to="/login" class="text-sm text-blue-600 hover:text-blue-500">
              ← Back to Login
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Footer />
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth, sendPasswordResetEmail } from "@/firebase";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";

onMounted(() => {
  document.title = "ElevatED – Password Reset";
});

const router = useRouter();
const email = ref("");
const errorMessage = ref("");
const successMessage = ref("");
const isSending = ref(false);

// Validation state
const emailError = ref("");

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Real-time email validation
const validateEmail = () => {
  if (!email.value.trim()) {
    emailError.value = "";
    return false;
  }
  if (!emailRegex.test(email.value)) {
    emailError.value = "Please enter a valid email address";
    return false;
  }
  emailError.value = "";
  return true;
};

const handlePasswordResetRequest = async () => {
  errorMessage.value = "";
  emailError.value = "";

  // Validate email
  const isEmailValid = validateEmail();
  if (!isEmailValid) {
    return;
  }

  if (!email.value.trim()) {
    errorMessage.value = "Please enter your email address.";
    return;
  }

  isSending.value = true;

  try {
    // Get the current domain for the action URL
    const actionUrl = `${window.location.origin}/password-reset`;

    await sendPasswordResetEmail(auth, email.value.trim(), {
      url: actionUrl,
      handleCodeInApp: false,
    });

    successMessage.value =
      "✅ Password reset email sent successfully! Check your inbox and spam folder for the reset link. The link will expire in 1 hour.";
    email.value = "";

    // Don't auto-redirect - let user choose when to go back
    // setTimeout(() => {
    //   router.push('/login');
    // }, 3000);
  } catch (error: any) {
    console.error("Password reset error:", error);

    if (error.code === "auth/user-not-found") {
      errorMessage.value = "No account found with this email address.";
    } else if (error.code === "auth/invalid-email") {
      errorMessage.value = "Please enter a valid email address.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage.value = "Too many requests. Please try again later.";
    } else if (error.code === "auth/network-request-failed") {
      errorMessage.value = "Network error. Please check your connection and try again.";
    } else if (error.code === "auth/user-disabled") {
      errorMessage.value = "This account has been disabled. Please contact support.";
    } else {
      errorMessage.value = `Failed to send reset email: ${error.message || "Please try again."}`;
    }
  } finally {
    isSending.value = false;
  }
};
</script>

<style scoped>
.login-container {
  box-shadow: 8px 8px 0px #000;
  max-width: 800px;
  margin: 0 auto;
}
</style>
